import { LeadTimeFormat } from '@snap-types/models/enums';
import { ERPDB } from '@snap-types/models/site/enums';
import { Nullish } from '@snap-types/types/nullish';
import { createContext, useContext } from 'react';

export type PDFQuoteLinePartType = {
  partId: string;
  erpDB: Nullish<ERPDB>;
  createdBySnap: boolean;
  customerPartId: Nullish<string>;
  description: Nullish<string>;
  drawingId: Nullish<string>;
  drawingRevision: Nullish<string>;
  partSpecs: Nullish<string>;
};

export type PDFQuoteLineType = {
  estQtyOnHand: Nullish<number>;
  id: string;
  /**
   * The shortest lead time across the line's prices, for documents that quote a
   * single value per line. Per-price values live on `quotePrices`.
   */
  leadTimeWeeks: Nullish<number>;
  leadTimeNotes: Nullish<string>;
  /**
   * Quantity-aware lines calculate a lead time per quantity break, so they
   * quote each one next to its price rather than collapsing to a single value.
   */
  isQuantityAwareLeadTime: boolean;
  lineNumber: number;
  specs: Nullish<string>;
  part: PDFQuoteLinePartType | null;
  partOptions: {
    id: string;
    name: string;
    groupName: string;
  }[];
  quotePrices: {
    id: string;
    quantity: string;
    unitPrice: string;
    total: string;
    /** Lead time in whole weeks for this quantity break. */
    leadTimeWeeks: Nullish<number>;
  }[];
  serviceCharge: {
    id: string;
    description: Nullish<string>;
  } | null;
};

export type PDFProviderType = {
  customer: {
    id: Nullish<string>;
    attentionName: Nullish<string>;
    name: Nullish<string>;
    address1: Nullish<string>;
    address2: Nullish<string>;
    city: Nullish<string>;
    state: Nullish<string>;
    postalCode: Nullish<string>;
    countryCode: Nullish<string>;
    email: Nullish<string>;
    phone: Nullish<string>;
    termsNetDays: Nullish<number>;
    salesRepId: Nullish<string>;
    salesRepName: Nullish<string>;
    salesRepEmail: Nullish<string>;
    salesRepPhone: Nullish<string>;
    customerQuoteId: Nullish<string>;
  };
  daysUntilExpiration: Nullish<number>;
  specs: Nullish<string>;
  quoteId: string;
  formattedQuoteTotal: string | null;
  quoteLines: PDFQuoteLineType[];
  leadTimeFormat: LeadTimeFormat;
  /** Lead times are held in production days; this converts them to weeks. */
  user: {
    firstName: string;
    lastName: string;
    email: string;
  };
  csrName: Nullish<string>;
  csrEmail: Nullish<string>;
  pricingUnit: string;
};

const PDFContext = createContext<PDFProviderType | null>(null);

type PDFProviderProps = PDFProviderType & {
  children: React.ReactNode;
};

export function PDFProvider({ children, ...props }: PDFProviderProps) {
  return <PDFContext.Provider value={props}>{children}</PDFContext.Provider>;
}

export function usePDFContext() {
  const context = useContext(PDFContext);

  if (!context) {
    throw new Error('usePDFContext must be used within a PDFProvider');
  }

  return context;
}
